import { useQuery } from '@tanstack/react-query';

import { CountryFlagWithLabel } from '@/components/CountryFlag';
import Icon from '@/components/Icon/Icon';
import { VStack, HStack } from '@/components/Layout';
import { Loader } from '@/components/Loading';
import SelectCountry from '@/components/Select/SelectCountry';
import { Text } from '@/components/Typography';
import { Button } from '@/components/ui/button';
import { SupportedCountry } from '@/constants/country';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { ApiResponseBody } from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { getPath } from '@/lib/api/path';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { FIVE_MINUTES } from '@/lib/query';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

import { Country } from './CFormCountryItem';

// API関連の型と関数をローカルで定義
type GetCountriesResponse = ApiResponseBody<'/v2/division/countries', 'get'>;

// 国データ取得API関数
const fetchCountries = async (): Promise<GetCountriesResponse> => {
  const res = await apiClient.GET('/v2/division/countries');
  return getDataOrThrowApiError(res);
};

// API レスポンスの国データの型
type ApiCountry = GetCountriesResponse['countries'][0];

/**
 * 国一覧を取得するReact Queryフック
 * CFormCountryItem専用のローカルフック
 */
function useCountries() {
  return useQuery({
    queryKey: [getPath('/v2/division/countries')] as const,
    queryFn: fetchCountries,
    staleTime: FIVE_MINUTES,
  });
}

type Props = CFormItemProps & {
  value?: (Country | undefined)[] | null;
  onChange?: (value?: (Country | undefined)[] | null) => void;
  /** 空の場合の表示テキスト */
  placeholder?: string;
  /** 空の選択を許可するか */
  allowEmpty?: boolean;
  /** 入力フィールドの幅 */
  inputWidth?: number | string;
};

/**
 * Card-based Form / MultiCountryItem
 */
export const CFormMultiCountryItem = buildCFormItem((props: Props) => {
  const {
    value,
    placeholder = '-',
    onChange,
    allowEmpty = false,
    readonly,
  } = props;

  // 国データを取得
  const { data: countriesData, isLoading, error } = useCountries();
  const tc = useCommonTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.country',
  });

  const handleAddNew = () => {
    onChange?.(value?.concat([undefined]) ?? []);
  };
  const handleRemove = (index: number) => {
    onChange?.(value?.filter((_, i) => i !== index) ?? []);
  };
  const handleChange = (index: number, country: Country | undefined) => {
    onChange?.(value?.map((v, i) => (i === index ? country : v)) ?? []);
  };

  // valueのcodeからnameを解決する関数
  const resolveCountryName = (country: Country | null): Country | null => {
    if (!country || !country.code) return country;

    // 既にnameがある場合はそのまま返す
    if (country.name) return country;

    // nameが空の場合、countriesDataから名前を解決
    if (countriesData?.countries) {
      const foundCountry = countriesData.countries.find(
        (c: ApiCountry) => c.code === country.code,
      );
      if (foundCountry) {
        return {
          ...country,
          name: foundCountry.name,
        };
      }
    }

    return country;
  };

  // ローディング中の表示
  if (isLoading) {
    return (
      <Button
        variant="outline"
        disabled
        className={cn('justify-start w-full', 'bg-neutral-fill')}
      >
        <Loader size="small" className="mr-2" />
        <Text variant="p" className="text-neutral-muted-on-fill">
          {t('loading')}
        </Text>
      </Button>
    );
  }

  // エラー時の表示
  if (error || !countriesData) {
    return (
      <Button
        variant="outline"
        disabled
        className={cn(
          'justify-start w-full text-error-on-neutral-fill border-error-border',
          'bg-neutral-fill',
        )}
      >
        <Text variant="p" className="text-error-on-neutral-fill">
          {t('loadFailed')}
        </Text>
      </Button>
    );
  }

  const readComponent = (() => {
    if (!value || value.length === 0)
      return (
        <Text variant="p" className="text-neutral-muted-on-fill">
          {placeholder}
        </Text>
      );

    return (
      <VStack>
        {value
          .map((v) => resolveCountryName(v ?? null))
          .filter((v) => v !== null)
          .map((country) => (
            <CountryFlagWithLabel
              key={country.code}
              countryCode={country.code}
              label={
                <Text
                  variant="labelSemiLarge"
                  className="text-neutral-on-fill truncate"
                >
                  {country.name}
                  {country.code && ` (${country.code})`}
                </Text>
              }
              className="min-w-0"
            />
          ))}
      </VStack>
    );
  })();

  return (
    <CFormItemBase
      {...props}
      readComponent={readComponent}
      writeComponent={
        readonly ? (
          readComponent
        ) : (
          <VStack className="gap-2 items-start">
            {value?.map((v, index) => (
              <HStack key={index} className="gap-2 items-center">
                <SelectCountry
                  value={v?.code as SupportedCountry | undefined}
                  onChange={(value?: keyof typeof SupportedCountry) => {
                    handleChange(index, value ? { code: value } : undefined);
                  }}
                  onClear={
                    allowEmpty
                      ? () => handleChange(index, undefined)
                      : undefined
                  }
                />
                <Button
                  variant="ghost"
                  className="p-2 w-8 h-8"
                  onClick={() => handleRemove(index)}
                >
                  <Icon iconName="Trash2Icon" />
                </Button>
              </HStack>
            ))}
            <Button size="sm" onClick={handleAddNew}>
              {tc('action.add')}
            </Button>
          </VStack>
        )
      }
    />
  );
});
